import { keyframes, mergeStyles, useTheme } from '@fluentui/react';
import { IPNav, IPNavLink, IPNavLinkGroup } from 'baseComponents/Nav/IPNav';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  Fragment,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useMemo,
  useRef,
  useState,
} from 'react';
import NavHintCallout, { INavHintCalloutHandle } from './NavHintCallout';
import { INavLinkOptions, toNavLink } from './navLinks';
import {
  buildNavStyles,
  hiddenScrollbar,
  navEdgeSpacing,
  NavGroupCaption,
  NavGroupRule,
  navRailClass,
  navScrollerThumbHidden,
  navScrollerThumbShown,
} from './navMainStyles';
import { findNavHintRow, useNavHintHover } from './useNavHintHover';
import { useNavModel } from './useNavModel';

//
// The Fluent v8 rendering of the main menu. Which entries exist, where they sit and which one is
// current comes from useNavModel; this file draws it, as the full menu or as the icon rail. The
// pinned groups at the bottom open in a callout beside their header, never in the menu.
//

/**
 * How one instance of the menu is drawn. 'full': labels, captions and children in place (the
 * expanded rail, the hover flyout, the mobile panel). 'icons': the compact rail; a task view, which
 * has no icon, shows as a dot.
 */
export type NavPresentation = 'full' | 'icons';

// Nested entries slide and fade in when their parent opens.
const nestedChildEnterKeyframe = keyframes({
  from: { opacity: 0, transform: 'translateY(-6px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
});

const nestedChildAnimationClass = mergeStyles({
  animationName: nestedChildEnterKeyframe,
  animationDuration: '0.24s',
  animationTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  animationFillMode: 'both',
});

// The icon rail draws this for a row without an icon of its own (a task view), so the rail keeps a
// row for every row of the flyout and the current view keeps its accent.
const navRowDotIcon = 'LocationDot';

export interface INavMainProps {
  presentation: NavPresentation;
}

const NavMain = (props: INavMainProps) => {
  const theme = useTheme();
  const compact = props.presentation === 'icons';
  // The flyout opens over the icon rail on the first pointer movement, so the pinned headers hint in
  // the full menu only.
  const footerHintable = props.presentation === 'full';
  const model = useNavModel();
  const hints = useNavHintHover();
  const calloutRef = useRef<INavHintCalloutHandle>(null);
  // Shows the scrollbar of the full menu. The fade in and out is CSS; see navScrollerThumbHidden.
  const [isScrollbarShown, setIsScrollbarShown] = useState<boolean>(false);
  // Fluent calls onLinkExpandClick right after onLinkClick for a row that has children. A click on
  // the row opens its page and opens a closed group, but never closes an open one; only the chevron
  // closes. The ref holds the key whose expand call must be skipped.
  const skipNextExpand = useRef<string | undefined>(undefined);

  const bodyLinkOptions = useMemo<INavLinkOptions>(
    () => ({ withLinks: true, hintable: false, iconForIconless: compact ? navRowDotIcon : undefined }),
    [compact],
  );
  const footerLinkOptions = useMemo<INavLinkOptions>(
    () => ({ withLinks: false, hintable: footerHintable }),
    [footerHintable],
  );

  const navStyles = useMemo(() => buildNavStyles(theme, compact), [compact, theme]);
  const { toggleGroup } = model;

  const bodyGroups = useMemo<IPNavLinkGroup[]>(
    () =>
      model.bodyGroups.map((group) => {
        const links = group.items.map((item) => toNavLink(item, bodyLinkOptions));
        const groupId = group.id;
        if (!group.isCollapsible || groupId === undefined) {
          return { name: group.caption, links };
        }

        return {
          name: group.caption,
          links,
          // Fluent overwrites onHeaderClick with a wrapper of its own and resolves isExpanded for
          // every group, so groupData is what tells the header renderer below which groups close.
          groupData: true,
          // Given isExpanded, Fluent leaves its own open state alone and follows the store.
          isExpanded: group.isExpanded,
          onHeaderClick: () => toggleGroup(groupId),
        };
      }),
    [bodyLinkOptions, model.bodyGroups, toggleGroup],
  );

  const footerGroups = useMemo<IPNavLinkGroup[]>(
    () => [{ links: model.footerItems.map((item) => toNavLink(item, footerLinkOptions)) }],
    [footerLinkOptions, model.footerItems],
  );

  // The position of each nested entry under its parent, for the staggered entrance.
  const nestedChildIndexById = useMemo(() => {
    const map = new Map<string, number>();
    model.bodyGroups.forEach((group) => {
      group.items.forEach((item) => {
        (item.children ?? []).forEach((child, index) => {
          map.set(child.id, index);
        });
      });
    });

    return map;
  }, [model.bodyGroups]);

  const onRenderGroupHeader = (
    group?: IPNavLinkGroup,
    defaultRender?: (innerGroup?: IPNavLinkGroup) => JSX.Element | null,
  ): JSX.Element | null => {
    if (!group?.name) {
      return null;
    }

    // The icon rail has no room for words: a rule marks the group. Its rows follow the same open
    // state, so a closed group is closed in both presentations and the rows stay aligned.
    if (compact) {
      return <NavGroupRule />;
    }

    // A group that closes keeps Fluent's own header, a button with the chevron, restyled to the
    // caption in navMainStyles. A group that always stays open is the plain caption.
    if (group.groupData === true && defaultRender) {
      return defaultRender(group);
    }

    return <NavGroupCaption text={group.name} />;
  };

  const onRenderLink = (
    link?: IPNavLink,
    defaultRender?: (innerLink?: IPNavLink) => JSX.Element | null,
  ): JSX.Element | null => {
    if (!link || !defaultRender) {
      return defaultRender ? defaultRender(link) : null;
    }

    const childIndex = link.key ? nestedChildIndexById.get(link.key) : undefined;
    if (childIndex !== undefined) {
      return (
        <span
          className={nestedChildAnimationClass}
          style={{ display: 'block', animationDelay: `${Math.min(childIndex, 6) * 0.03}s` }}
        >
          {defaultRender(link)}
        </span>
      );
    }

    return defaultRender(link);
  };

  const onLinkClick = (ev?: ReactMouseEvent<HTMLElement>, link?: IPNavLink): void => {
    if (!link?.key) {
      return;
    }

    const isPinnedHeader = model.footerItems.some((header) => header.id === link.key);
    if (!isPinnedHeader) {
      if (link.links && link.links.length > 0 && link.isExpanded) {
        skipNextExpand.current = link.key;
      }

      model.selectItem(link.key);
      hints.close();

      return;
    }

    // A pinned header has no page; its pages are in the callout. The hover has usually opened it
    // already. A keyboard activation (a click without a pointer) also moves focus into it.
    const row = findNavHintRow(ev?.currentTarget ?? null);
    if (row) {
      hints.open(row, { focus: ev?.detail === 0 });
    }
  };

  const onLinkExpandClick = (ev?: ReactMouseEvent<HTMLElement>, link?: IPNavLink): void => {
    // Without this Fluent also flips its own copy of the flag.
    ev?.preventDefault();
    if (!link?.key) {
      return;
    }

    if (skipNextExpand.current === link.key) {
      skipNextExpand.current = undefined;

      return;
    }

    model.toggleExpanded(link.key);
  };

  const onHintSelect = (id: string): void => {
    model.selectItem(id);
    hints.close();
  };

  const onRailKeyDown = (ev: ReactKeyboardEvent<HTMLElement>): void => {
    // ArrowRight on the open hint's header moves the keyboard into the callout.
    if (ev.key === 'ArrowRight' && hints.hint && findNavHintRow(ev.target)?.id === hints.hint.id) {
      ev.preventDefault();
      calloutRef.current?.focus();

      return;
    }

    hints.railHandlers.onKeyDown(ev);
  };

  const onRailFocus = (ev: ReactFocusEvent<HTMLElement>): void => {
    setIsScrollbarShown(true);
    hints.railHandlers.onFocus(ev);
  };

  const onRailBlur = (ev: ReactFocusEvent<HTMLElement>): void => {
    // Only when focus leaves the menu, not when it moves between rows.
    if (!ev.currentTarget.contains(ev.relatedTarget)) {
      setIsScrollbarShown(false);
    }
  };

  const onRailMouseLeave = (): void => {
    setIsScrollbarShown(false);
    hints.railHandlers.onMouseLeave();
  };

  const openHintId = hints.hint?.id;
  const openHeader = openHintId ? model.footerItems.find((header) => header.id === openHintId) : undefined;

  let scrollerClassName: string | undefined = undefined;
  if (!compact) {
    scrollerClassName = isScrollbarShown ? navScrollerThumbShown : navScrollerThumbHidden;
  }

  //
  // Main render
  //
  return (
    <Fragment>
      <IPStack
        verticalFill
        className={navRailClass}
        styles={{ root: { overflow: 'hidden' } }}
        onMouseEnter={() => setIsScrollbarShown(true)}
        onMouseLeave={onRailMouseLeave}
        onMouseOver={hints.railHandlers.onMouseOver}
        onMouseMove={hints.railHandlers.onMouseMove}
        onFocus={onRailFocus}
        onBlur={onRailBlur}
        onKeyDown={onRailKeyDown}
      >
        <IPStack.Item
          grow
          // The scroller's own class changes with the state; see navScrollerThumbHidden.
          className={scrollerClassName}
          styles={{
            root: {
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingTop: navEdgeSpacing,
              ...(compact ? hiddenScrollbar : {}),
            },
          }}
        >
          <IPNav
            selectedKey={model.selectedId}
            styles={navStyles}
            groups={bodyGroups}
            onRenderGroupHeader={onRenderGroupHeader}
            onRenderLink={onRenderLink}
            onLinkClick={onLinkClick}
            onLinkExpandClick={onLinkExpandClick}
          />
        </IPStack.Item>
        {model.footerItems.length > 0 && (
          <IPStack.Item
            disableShrink
            styles={{
              root: {
                borderTop: `1px solid ${theme.semanticColors.bodyDivider}`,
                paddingTop: 8,
                marginTop: 8,
              },
            }}
          >
            <IPNav
              selectedKey={model.selectedId}
              styles={navStyles}
              groups={footerGroups}
              onLinkClick={onLinkClick}
              onLinkExpandClick={onLinkExpandClick}
            />
          </IPStack.Item>
        )}
      </IPStack>
      {/* A sibling of the rail, not a child: React routes a portal's events through its React
          parents, and the rail's hover handlers must not see the callout's rows. */}
      {hints.hint && openHeader && (
        <NavHintCallout
          ref={calloutRef}
          target={hints.hint.element}
          header={openHeader}
          selectedKey={model.matchedId}
          focusOnOpen={hints.focusRequested}
          onSelect={onHintSelect}
          onToggleExpanded={model.toggleExpanded}
          onDismiss={hints.close}
          onMouseEnter={hints.calloutHandlers.onMouseEnter}
          onMouseLeave={hints.calloutHandlers.onMouseLeave}
        />
      )}
    </Fragment>
  );
};

export default NavMain;
